import classnames from 'classnames';
import { FC, useCallback, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { nullFn } from 'constants/common';

interface CustomTextAreaProps {
  id: string;
  type?: string;
  placeholder?: string;
  className?: string;
  value: string;
  name: string;
  onChange: any;
  maxLength?: number;
  resizeTextArea?: boolean;
  width?: string;
  isInputValid?: boolean;
  warningMessageText?: string;
  disabled?: boolean;
  onClick?: () => void;
  minHeight?: string;
  maxHeight?: number;
  onFocus?: () => void;
  showRemainingChar?: boolean;
  autoFocus?: boolean;
  remainingCharClassName?: string;
  onPasteImage?: (file: File) => void;
}

const CustomTextArea: FC<CustomTextAreaProps> = ({
  resizeTextArea = false,
  id,
  maxLength,
  onChange,
  className,
  placeholder,
  name,
  value,
  width = null,
  isInputValid = true,
  warningMessageText = '',
  disabled = false,
  onClick = null,
  minHeight = '50px',
  maxHeight = 100,
  onFocus = nullFn,
  autoFocus = false,
  showRemainingChar = false,
  remainingCharClassName = '',
  onPasteImage
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      if (!onPasteImage) return;

      const items = event.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            event.preventDefault();
            onPasteImage(file);
            return;
          }
        }
      }
    },
    [onPasteImage]
  );

  useEffect(() => {
    if (resizeTextArea && textAreaRef.current) {
      // Reset height to auto first to get the correct scrollHeight
      textAreaRef.current.style.height = 'auto';

      if (!value || value.length < 1) {
        textAreaRef.current.style.height = minHeight;
      } else {
        const scrollHeight = textAreaRef.current.scrollHeight;
        textAreaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
      }
    }
  }, [value, resizeTextArea, minHeight, maxHeight]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
  };

  const widthStyles = width ? { width } : {};
  return (
    <div
      className={'flex flex-col'}
      onClick={() => {
        if (disabled && onClick) {
          onClick();
        }
      }}>
      <textarea
        id={id}
        ref={textAreaRef}
        maxLength={maxLength}
        name={name}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        className={twMerge(
          classnames(
            `customNormalScroll h-[50px] max-h-[100px] rounded bg-secondaryBtn 
           ${!isInputValid ? 'ring-2 ring-red-500 focus:ring-2 focus:ring-red-500' : ''}
            p-4 text-[15px] font-semibold ${disabled ? 'text-accentText' : 'text-primaryText'}
             placeholder:text-[15px] placeholder:font-normal placeholder:text-accentText`
          ),
          className
        )}
        style={{ ...widthStyles }}
        onFocus={onFocus}
        onPaste={handlePaste}
        autoFocus={autoFocus}
      />
      {showRemainingChar && maxLength && (
        <div className={`text-right text-px13 font-normal text-accentText ${remainingCharClassName}`}>
          {value.length}/{maxLength} characters
        </div>
      )}
    </div>
  );
};

export default CustomTextArea;
